
import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { Shield, Upload } from 'lucide-react';
import { toast } from 'sonner';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireKYC?: boolean;
  actionType?: 'view_listing' | 'list_property' | 'general';
}

const ProtectedRoute = ({ children, requireKYC = false, actionType = 'general' }: ProtectedRouteProps) => {
  const { user, profile } = useAuth();
  const { t } = useLanguage();

  useEffect(() => {
    if (!user) {
      // Store the message in sessionStorage to show on sign-in page
      switch (actionType) {
        case 'view_listing':
          sessionStorage.setItem('redirectMessage', JSON.stringify({
            type: 'error',
            title: t('sign_in_to_view', 'يجب تسجيل الدخول لمشاهدة العقار', 'Please sign in to view this listing'),
            description: t('sign_in_view_desc', 'سجل دخولك للوصول إلى تفاصيل العقار والتواصل مع المالك', 'Sign in to access listing details and contact the owner')
          }));
          break;
        case 'list_property':
          sessionStorage.setItem('redirectMessage', JSON.stringify({
            type: 'error',
            title: t('sign_in_to_list', 'يجب تسجيل الدخول لإدراج عقار', 'Please sign in to list a property'),
            description: t('sign_in_list_desc', 'أنشئ حساباً أو سجل دخولك لإدراج عقارك', 'Create an account or sign in to list your property')
          }));
          break;
        default:
          sessionStorage.setItem('redirectMessage', JSON.stringify({
            type: 'error', 
            title: t('sign_in_required', 'يجب تسجيل الدخول', 'Please sign in to continue'),
            description: t('sign_in_required_desc', 'سجل دخولك للوصول إلى هذه الصفحة', 'Sign in to access this page')
          }));
      }
    }
  }, [user, actionType, t]);

  if (!user) {
    return <Navigate to="/signin" replace />;
  }

  // Check if KYC verification is required and user is not verified
  const isVerified = profile?.kyc_status === 'VERIFIED';
  const hasKYCDocuments = profile?.id_front_url && profile?.id_back_url;
  
  if (requireKYC && !isVerified && !hasKYCDocuments) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 to-accent/5 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <Shield className="h-12 w-12 mx-auto text-yellow-500 mb-4" />
            <CardTitle className="text-xl">
              {t('verification_required_contact', 'التحقق مطلوب للتواصل', 'Verification Required to Contact')}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-muted-foreground">
              {t('verification_required_message', 'يجب التحقق من هويتك للتواصل مع أصحاب العقارات', 'You must verify your identity to contact property owners')}
            </p>
            
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <div className="flex items-center gap-2 text-yellow-800 mb-2">
                <Upload className="h-4 w-4" />
                <span className="font-medium">
                  {t('verification_options', 'خيارات التحقق', 'Verification Options')}
                </span>
              </div>
              <ul className="text-sm text-yellow-700 text-left space-y-1">
                <li>• {t('upload_id_documents', 'رفع مستندات الهوية', 'Upload ID documents')}</li>
                <li>• {t('wait_admin_verification', 'انتظار التحقق من الإدارة', 'Wait for admin verification')}</li>
              </ul>
            </div>

            <Button onClick={() => window.location.href = '/account'} className="w-full">
              {t('upload_documents', 'رفع المستندات', 'Upload Documents')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
